import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiX, FiSave, FiUpload, FiChevronLeft, FiChevronRight, FiTrash2 } from 'react-icons/fi';
import { useAuthStore } from '../store/useAuthStore';
import '../styles/ProductEditor.css';

const API_BASE_URL = process.env.REACT_APP_API_URL;

export default function ProductEditor() {
  const navigate = useNavigate();
  const { productId } = useParams();
  const token = useAuthStore((state) => state.token);
  const user = useAuthStore((state) => state.user);

  const [product, setProduct] = useState({
    name: '',
    description: '',
    category: 'Mens',
    price: '',
    discountPrice: 0,
    sku: '',
    stockByColor: {}, // Structure: { [colorId]: { [size]: quantity } }
  });

  const [discountInput, setDiscountInput] = useState({
    type: 'price',
    value: 0
  });

  const [categories, setCategories] = useState(['Mens', 'Womens', 'Kids', 'Sports']);

  const [enabledSizes, setEnabledSizes] = useState({});
  const [images, setImages] = useState([]);
  const [colors, setColors] = useState([]);
  const [availableColors, setAvailableColors] = useState([]); // All global colors
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [newImages, setNewImages] = useState([]);
  const [newColor, setNewColor] = useState({ name: '', hex: '#000000' });
  const [addingNewColor, setAddingNewColor] = useState(false); // Toggle between existing and new
  const [selectedExistingColor, setSelectedExistingColor] = useState(''); // Selected existing color ID
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  // Staging state for pending image color assignments
  // Structure: { [imageId]: colorId }
  const [pendingImageColorAssignments, setPendingImageColorAssignments] = useState({});

  // State for color replacement dialog
  const [showReplaceColorDialog, setShowReplaceColorDialog] = useState(false);
  const [colorToReplace, setColorToReplace] = useState(null);
  const [replacementColorId, setReplacementColorId] = useState('');

  // Redirect if not admin
  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/login');
    }
  }, [user, navigate]);

  // Load categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/categories`);
        if (response.ok) {
          const data = await response.json();
          // Extract just the names from the category objects
          const categoryNames = data.map(cat => cat.name);
          if (Array.isArray(categoryNames) && categoryNames.length > 0) {
            setCategories(categoryNames);
          }
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        // Fall back to defaults if API fails
        setCategories(['Mens', 'Womens', 'Kids', 'Sports']);
      }
    };

    fetchCategories();
  }, []);

  // Load available colors from API
  useEffect(() => {
    const fetchColors = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/colors`);
        if (response.ok) {
          const data = await response.json();
          setAvailableColors(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error('Error fetching colors:', error);
        setAvailableColors([]);
      }
    };

    fetchColors();
  }, []);

  // Fetch product details if editing
  useEffect(() => {
    if (!productId || productId === 'new') {
      setLoading(false);
      return;
    }

    const fetchProductDetails = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/products/with-images?id=${productId}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          const productData = Array.isArray(data) ? data[0] : data;
          const stockByColor = productData.stockByColor || {};

          // Initialize enabledSizes based on which sizes have stock (from all colors)
          const newEnabledSizes = {};
          Object.values(stockByColor).forEach(colorStock => {
            Object.keys(colorStock).forEach(size => {
              newEnabledSizes[size] = true;
            });
          });

          const discountPrice = productData.discount_price || 0;
          setProduct({
            name: productData.name || '',
            description: productData.description || '',
            category: productData.category || 'Mens',
            price: productData.price || '',
            discountPrice,
            sku: productData.sku || '',
            stockByColor,
          });
          // Set discount input with the actual discount price value
          setDiscountInput({ type: 'price', value: discountPrice.toString() });
          setEnabledSizes(newEnabledSizes);
          setImages(productData.images || []);
          setColors(productData.colors || []);
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        setMessage('Error loading product');
      } finally {
        setLoading(false);
      }
    };

    fetchProductDetails();
  }, [productId, token]);

  // Helper to refresh product data (used after upload/save operations)
  const refreshProductData = useCallback(async () => {
    if (!productId || productId === 'new') {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/products/with-images?id=${productId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        const productData = Array.isArray(data) ? data[0] : data;
        setImages(productData.images || []);
        setColors(productData.colors || []);
      }
    } catch (error) {
      console.error('Error refreshing product:', error);
    } finally {
      setLoading(false);
    }
  }, [productId, token]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProduct((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    setNewImages((prev) => [...prev, ...files]);
    // Reset input value so same file can be selected again
    e.target.value = '';
  };

  const handleUploadWithoutSaving = async (e) => {
    e.preventDefault();
    if (newImages.length === 0) return;

    setSaving(true);
    setMessage('');

    try {
      for (let i = 0; i < newImages.length; i++) {
        const file = newImages[i];
        const formData = new FormData();
        formData.append('file', file);
        formData.append('productId', productId);
        formData.append('isPrimary', i === 0 && images.length === 0 ? 'true' : 'false');

        const uploadResponse = await fetch(`${API_BASE_URL}/api/products/image`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData,
        });

        if (!uploadResponse.ok) {
          throw new Error(`Failed to upload image ${i + 1}`);
        }
      }

      setMessage('Images uploaded successfully!');
      setNewImages([]);

      // Refresh product data
      if (productId && productId !== 'new') {
        await refreshProductData();
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      setMessage(`Error: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveNewImage = (index) => {
    setNewImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleMoveImageUp = (index) => {
    if (index > 0) {
      const newArray = [...images];
      [newArray[index], newArray[index - 1]] = [newArray[index - 1], newArray[index]];
      setImages(newArray);
      setCurrentImageIndex(index - 1);
    }
  };

  const handleMoveImageDown = (index) => {
    if (index < images.length - 1) {
      const newArray = [...images];
      [newArray[index], newArray[index + 1]] = [newArray[index + 1], newArray[index]];
      setImages(newArray);
      setCurrentImageIndex(index + 1);
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Delete this image?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/products/images/${imageId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        setImages((prev) => {
          const updatedImages = prev.filter((img) => img.id !== imageId);
          // Update current image index after deletion
          if (currentImageIndex >= updatedImages.length) {
            setCurrentImageIndex(Math.max(0, updatedImages.length - 1));
          }
          return updatedImages;
        });
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      setMessage('Error deleting image');
    }
  };

  const handleNextImage = () => {
    const totalImages = images.length + newImages.length;
    if (totalImages > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % totalImages);
    }
  };

  const handlePrevImage = () => {
    const totalImages = images.length + newImages.length;
    if (totalImages > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + totalImages) % totalImages);
    }
  };

  const handleAddColor = async () => {
    setSaving(true);
    try {
      if (addingNewColor) {
        // Adding a new color
        if (!newColor.name.trim()) {
          setMessage('Please enter a color name');
          setSaving(false);
          return;
        }

        const response = await fetch(`${API_BASE_URL}/api/products/${productId}/colors`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            isNewColor: true,
            color_name: newColor.name,
            color_hex: newColor.hex,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          setColors((prev) => [...prev, {
            id: data.id,
            color_name: data.color_name,
            color_hex: data.color_hex,
            display_order: data.display_order,
          }]);
          setNewColor({ name: '', hex: '#000000' });
          setAddingNewColor(false);
          // Refresh available colors
          const colorsResponse = await fetch(`${API_BASE_URL}/api/colors`);
          if (colorsResponse.ok) {
            setAvailableColors(await colorsResponse.json());
          }
          setMessage('Color added successfully!');
        } else {
          const errorData = await response.json();
          setMessage(errorData.message || 'Failed to add color');
        }
      } else {
        // Adding an existing color
        if (!selectedExistingColor) {
          setMessage('Please select a color');
          setSaving(false);
          return;
        }

        const response = await fetch(`${API_BASE_URL}/api/products/${productId}/colors`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            isNewColor: false,
            color_id: parseInt(selectedExistingColor),
          }),
        });

        if (response.ok) {
          const data = await response.json();
          setColors((prev) => [...prev, {
            id: data.id,
            color_name: data.color_name,
            color_hex: data.color_hex,
            display_order: data.display_order,
          }]);
          setSelectedExistingColor('');
          setMessage('Color added successfully!');
        } else {
          const errorData = await response.json();
          setMessage(errorData.message || 'Failed to add color');
        }
      }
    } catch (error) {
      console.error('Error adding color:', error);
      setMessage('Error adding color');
    } finally {
      setSaving(false);
    }
  };

  const getColorInventoryCount = (colorId) => {
    if (!product.stockByColor || !product.stockByColor[colorId]) {
      return 0;
    }
    const colorStock = product.stockByColor[colorId];
    return Object.values(colorStock).reduce((sum, qty) => sum + (qty || 0), 0);
  };

  const handleRemoveColor = async (colorId) => {
    const inventoryCount = getColorInventoryCount(colorId);

    if (inventoryCount > 0) {
      alert(`This color has ${inventoryCount} inventory items. Please use Replace to transfer inventory to another color, or manually reduce inventory to 0 before deleting.`);
      return;
    }

    if (!window.confirm('Are you sure you want to remove this color from this product?')) return;

    // Delete color from database
    try {
      const response = await fetch(`${API_BASE_URL}/api/products/${productId}/colors/${colorId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || 'Failed to delete color from database');
      }

      // Remove color from product's stockByColor
      const newStockByColor = { ...product.stockByColor };
      delete newStockByColor[colorId];

      setProduct({ ...product, stockByColor: newStockByColor });
      setColors((prev) => prev.filter((c) => c.id !== colorId));
      setMessage('Color removed from product');
    } catch (error) {
      console.error('Error deleting color:', error);
      setMessage(`Error deleting color: ${error.message}`);
    }
  };

  const handleReplaceColor = () => {
    if (!replacementColorId) {
      alert('Please select a replacement color');
      return;
    }

    const oldColorId = colorToReplace.id;
    const inventoryCount = getColorInventoryCount(oldColorId);

    if (inventoryCount === 0) {
      alert('This color has no inventory. Use Delete instead.');
      setShowReplaceColorDialog(false);
      return;
    }

    // Transfer inventory from old color to new color
    const newStockByColor = JSON.parse(JSON.stringify(product.stockByColor));
    const oldColorInventory = newStockByColor[oldColorId];

    // Add old color's inventory to new color (or initialize if not present)
    if (!newStockByColor[replacementColorId]) {
      newStockByColor[replacementColorId] = {};
    }

    // Merge inventories - add old color quantities to new color
    Object.entries(oldColorInventory).forEach(([size, qty]) => {
      newStockByColor[replacementColorId][size] = (newStockByColor[replacementColorId][size] || 0) + qty;
    });

    // Remove old color
    delete newStockByColor[oldColorId];

    setProduct({ ...product, stockByColor: newStockByColor });
    setColors((prev) => prev.filter((c) => c.id !== oldColorId));
    setShowReplaceColorDialog(false);
    setColorToReplace(null);
    setReplacementColorId('');
    setMessage(`Color replaced successfully. ${inventoryCount} inventory items transferred.`);
  };

  const handleAssignImageColor = (imageId, colorId) => {
    // Stage the color assignment - don't save to backend yet
    setPendingImageColorAssignments((prev) => ({
      ...prev,
      [imageId]: colorId || null
    }));

    // Update local state for immediate UI feedback
    setImages((prev) =>
      prev.map((img) =>
        img.id === imageId ? { ...img, color_id: colorId || null } : img
      )
    );

    setMessage('Color assignment staged. Click Save to apply changes.');
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      // Debug: Log what we're saving
      console.log('Product state before save:', product);
      console.log('Saving product:', {
        name: product.name,
        price: product.price,
        discountPrice: product.discountPrice,
        category: product.category,
        hasStockByColor: !!product.stockByColor,
        stockByColorKeys: Object.keys(product.stockByColor || {}),
        stockByColorLength: Object.keys(product.stockByColor || {}).length,
      });

      // Update product info
      if (productId && productId !== 'new') {
        // Clean up product data to only send what backend needs
        const productDataToSend = {
          name: product.name,
          description: product.description,
          category: product.category,
          price: product.price,
          discountPrice: product.discountPrice,
          sku: product.sku,
          stockByColor: product.stockByColor,
        };
        console.log('Sending product data to backend:', JSON.stringify(productDataToSend));
        const response = await fetch(`${API_BASE_URL}/api/products/${productId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(productDataToSend),
        });

        if (!response.ok) {
          throw new Error('Failed to update product');
        }

        // Save image order
        if (images.length > 0) {
          const imageOrderData = images.map((img, index) => ({
            id: img.id,
            display_order: index,
            is_primary: index === 0 ? 1 : 0,
          }));

          const orderResponse = await fetch(`${API_BASE_URL}/api/products/${productId}/image-order`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ images: imageOrderData }),
          });

          if (!orderResponse.ok) {
            console.warn('Failed to save image order, continuing anyway');
          }
        }

        // Save pending image color assignments
        if (Object.keys(pendingImageColorAssignments).length > 0) {
          console.log('Saving pending image color assignments:', pendingImageColorAssignments);
          for (const [imageId, colorId] of Object.entries(pendingImageColorAssignments)) {
            try {
              console.log(`Assigning color ${colorId} to image ${imageId}`);
              const colorResponse = await fetch(`${API_BASE_URL}/api/products/images/${imageId}/assign-color`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ colorId: colorId || null }),
              });

              if (!colorResponse.ok) {
                const errorData = await colorResponse.json().catch(() => ({ error: 'Unknown error' }));
                console.error(`Failed to assign color to image ${imageId}:`, colorResponse.status, errorData);
                throw new Error(`Failed to assign color to image: ${errorData.error || errorData.message || 'Unknown error'}`);
              } else {
                const successData = await colorResponse.json();
                console.log(`Successfully assigned color to image ${imageId}:`, successData);
              }
            } catch (colorError) {
              console.error(`Error assigning color to image ${imageId}:`, colorError);
              throw colorError; // Re-throw to stop the save process
            }
          }
          // Clear pending assignments after saving
          setPendingImageColorAssignments({});
        }
      }

      // Upload new images
      if (newImages.length > 0) {
        for (let i = 0; i < newImages.length; i++) {
          const file = newImages[i];
          const formData = new FormData();
          formData.append('file', file);
          formData.append('productId', productId);
          formData.append('isPrimary', i === 0 && images.length === 0 ? 'true' : 'false');

          const uploadResponse = await fetch(`${API_BASE_URL}/api/products/image`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData,
          });

          if (!uploadResponse.ok) {
            throw new Error(`Failed to upload image ${i + 1}`);
          }
        }
      }

      setMessage('Product saved successfully!');
      setNewImages([]);

      // Refresh product data
      if (productId && productId !== 'new') {
        await refreshProductData();
      }

      setTimeout(() => {
        navigate('/admin');
      }, 1500);
    } catch (error) {
      console.error('Error saving product:', error);
      setMessage(`Error: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const currentImage =
    currentImageIndex < images.length
      ? images[currentImageIndex]
      : newImages[currentImageIndex - images.length];

  const currentImageUrl =
    currentImage instanceof File
      ? URL.createObjectURL(currentImage)
      : currentImage?.image_url;

  const totalImages = images.length + newImages.length;

  if (loading) {
    return <div className="product-editor-loading">Loading product...</div>;
  }

  return (
    <div className="product-editor-overlay">
      <div className="product-editor-container">
        {/* Header */}
        <div className="product-editor-header">
          <h1>{productId === 'new' ? 'Create New Product' : 'Edit Product'}</h1>
          <button className="btn-close" onClick={() => navigate('/admin')}>
            <FiX size={24} />
          </button>
        </div>

        {message && (
          <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
            {message}
          </div>
        )}

        <form onSubmit={handleSaveProduct} className="product-editor-form">
          <div className="editor-grid">
            {/* Left: Image Section */}
            <div className="editor-left">
              <div className="image-editor-section">
                <h2>Product Images</h2>

                <div className="image-carousel">
                  {totalImages > 0 ? (
                    <>
                      <div className="image-display">
                        <img src={currentImageUrl} alt="Product" />
                      </div>
                      {totalImages > 1 && (
                        <>
                          <button type="button" className="carousel-btn prev" onClick={handlePrevImage}>
                            <FiChevronLeft size={24} />
                          </button>
                          <button type="button" className="carousel-btn next" onClick={handleNextImage}>
                            <FiChevronRight size={24} />
                          </button>
                          <div className="image-counter">
                            {currentImageIndex + 1} / {totalImages}
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <div className="image-placeholder">
                      <p>No images yet. Upload one to get started!</p>
                    </div>
                  )}
                </div>

                {/* Delete Current Image Button */}
                {currentImageIndex < images.length && (
                  <button
                    type="button"
                    className="btn-delete-current-image"
                    onClick={() => handleDeleteImage(images[currentImageIndex].id)}
                  >
                    <FiTrash2 /> Delete Current Image
                  </button>
                )}

                {/* Existing Images Order */}
                {images.length > 0 && (
                  <div className="existing-images-section">
                    <h3>Product Images ({images.length})</h3>
                    <p className="image-order-hint">Reorder images by dragging or using arrows. First image is primary. Assign colors to images so they appear when that color is selected.</p>
                    <div className="images-order-list">
                      {images.map((image, index) => (
                        <div key={image.id} className="image-order-item">
                          <img src={image.image_url || image.image} alt={`Product ${index + 1}`} />
                          <div className="image-order-info">
                            <span className="image-order-number">{index + 1}</span>
                            {index === 0 && <span className="badge-primary">Primary</span>}
                            {index === 1 && <span className="badge-secondary">Secondary</span>}
                          </div>
                          <div className="image-color-selector">
                            <label htmlFor={`color-select-${image.id}`} className="color-selector-label">
                              Color:
                            </label>
                            <select
                              id={`color-select-${image.id}`}
                              className="color-select-input"
                              value={image.color_id || ''}
                              onChange={(e) => handleAssignImageColor(image.id, e.target.value ? parseInt(e.target.value) : null)}
                            >
                              <option value="">No Color</option>
                              {colors.map((color) => (
                                <option key={color.id} value={color.id}>
                                  {color.color_name}
                                </option>
                              ))}
                            </select>
                            {image.color_id && (
                              <div
                                className="color-preview"
                                style={{ backgroundColor: colors.find((c) => c.id === image.color_id)?.color_hex }}
                                title={colors.find((c) => c.id === image.color_id)?.color_name}
                              ></div>
                            )}
                          </div>
                          <div className="image-order-controls">
                            <button
                              type="button"
                              className="btn-arrow"
                              onClick={() => handleMoveImageUp(index)}
                              disabled={index === 0}
                              title="Move up"
                            >
                              ▲
                            </button>
                            <button
                              type="button"
                              className="btn-arrow"
                              onClick={() => handleMoveImageDown(index)}
                              disabled={index === images.length - 1}
                              title="Move down"
                            >
                              ▼
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* New Images Preview */}
                {newImages.length > 0 && (
                  <div className="new-images-preview">
                    <h3>New Images to Upload</h3>
                    <div className="images-grid">
                      {newImages.map((file, index) => (
                        <div key={index} className="image-preview-item">
                          <img src={URL.createObjectURL(file)} alt={`new-${index}`} />
                          <button
                            type="button"
                            className="btn-remove-image"
                            onClick={() => handleRemoveNewImage(index)}
                            title="Remove this image"
                          >
                            <FiX />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upload Images */}
                <div className="upload-section">
                  <label htmlFor="image-input" className="btn-upload-images">
                    <FiUpload /> Add More Images
                  </label>
                  <input
                    id="image-input"
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageSelect}
                    style={{ display: 'none' }}
                  />
                  {newImages.length > 0 && (
                    <button
                      type="button"
                      className="btn-upload-now"
                      onClick={handleUploadWithoutSaving}
                      disabled={saving}
                    >
                      <FiUpload /> {saving ? 'Uploading...' : 'Upload Images'}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Right: Product Details */}
            <div className="editor-right">
              <div className="product-details-section">
                <h2>Product Details</h2>

                <div className="form-group">
                  <label htmlFor="name">Product Name *</label>
                  <input
                    id="name"
                    type="text"
                    name="name"
                    value={product.name}
                    onChange={handleInputChange}
                    placeholder="Enter product name"
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="category">Category *</label>
                    <select
                      id="category"
                      name="category"
                      value={product.category}
                      onChange={handleInputChange}
                      required
                    >
                      {categories.map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="price">Price *</label>
                    <input
                      id="price"
                      type="number"
                      step="0.01"
                      name="price"
                      value={product.price}
                      onChange={handleInputChange}
                      placeholder="0.00"
                      required
                    />
                  </div>
                </div>

                <fieldset>
                  <legend>Discount (Optional)</legend>
                  <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="radio"
                        name="discountType"
                        value="price"
                        checked={discountInput.type === 'price'}
                        onChange={(e) => {
                          // When switching to price mode, use current discount price
                          setDiscountInput({ type: 'price', value: product.discountPrice.toString() });
                        }}
                      />
                      Discounted Price
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="radio"
                        name="discountType"
                        value="percentage"
                        checked={discountInput.type === 'percentage'}
                        onChange={(e) => {
                          // When switching to percentage mode, calculate percentage from discount price
                          const price = parseFloat(product.price) || 0;
                          const discountPrice = parseFloat(product.discountPrice) || 0;
                          const percentage = price > 0 ? ((price - discountPrice) / price * 100).toFixed(2) : 0;
                          setDiscountInput({ type: 'percentage', value: percentage.toString() });
                        }}
                      />
                      Discount %
                    </label>
                  </div>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    {discountInput.type === 'price' ? (
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Discounted Price"
                        value={discountInput.value}
                        onChange={(e) => {
                          const discountPrice = parseFloat(e.target.value) || 0;
                          setDiscountInput({ type: 'price', value: e.target.value });
                          setProduct({ ...product, discountPrice });
                        }}
                      />
                    ) : (
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        placeholder="Discount % (0-100)"
                        value={discountInput.value}
                        onChange={(e) => {
                          const price = parseFloat(product.price) || 0;
                          const percentage = parseFloat(e.target.value) || 0;
                          const discountPrice = price * (1 - percentage / 100);
                          setDiscountInput({ type: 'percentage', value: e.target.value });
                          setProduct({ ...product, discountPrice: Math.round(discountPrice * 100) / 100 });
                        }}
                      />
                    )}
                    <span style={{ alignSelf: 'center', color: '#666', fontSize: '0.9rem' }}>
                      {product.discountPrice > 0 ? `→ $${product.discountPrice.toFixed(2)}` : 'No discount'}
                    </span>
                  </div>
                </fieldset>

                <div className="form-group">
                  <label htmlFor="sku">SKU (Stock Keeping Unit) *</label>
                  <input
                    id="sku"
                    type="text"
                    name="sku"
                    value={product.sku}
                    onChange={handleInputChange}
                    placeholder="e.g., HS-001-BLK"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="description">Description (HTML supported)</label>
                  <textarea
                    id="description"
                    name="description"
                    value={product.description}
                    onChange={handleInputChange}
                    placeholder="Enter product description. HTML tags (e.g., <p>, <br>, <strong>, <ul>) are supported."
                    rows="6"
                  ></textarea>
                </div>

                {/* Colors Management */}
                <fieldset className="colors-section">
                  <legend>Product Colors</legend>

                  {/* Existing Colors */}
                  {colors.length > 0 && (
                    <div className="colors-list">
                      <h3>Added Colors</h3>
                      <div className="colors-grid">
                        {colors.map((color) => {
                          const inventoryCount = getColorInventoryCount(color.id);
                          const hasInventory = inventoryCount > 0;
                          return (
                            <div key={color.id} className="color-item">
                              <div className="color-display">
                                <div
                                  className="color-swatch"
                                  style={{ backgroundColor: color.color_hex }}
                                  title={color.color_hex}
                                ></div>
                                <div className="color-info">
                                  <span className="color-label">{color.color_name}</span>
                                  {hasInventory && (
                                    <span className="color-inventory-warning" title="This color has inventory. Clear it before removing.">
                                      {inventoryCount} in stock
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: '0.5rem' }}>
                                {hasInventory && (
                                  <button
                                    type="button"
                                    className="btn-replace-color"
                                    onClick={() => {
                                      setColorToReplace(color);
                                      setShowReplaceColorDialog(true);
                                    }}
                                    title={`Replace this color and transfer ${inventoryCount} items to another color`}
                                  >
                                    ↻ Replace
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="btn-remove-color"
                                  onClick={() => handleRemoveColor(color.id)}
                                  title={hasInventory ? `Cannot remove - has ${inventoryCount} items in inventory. Use Replace instead.` : 'Remove color'}
                                  disabled={hasInventory}
                                  style={{ opacity: hasInventory ? 0.5 : 1, cursor: hasInventory ? 'not-allowed' : 'pointer' }}
                                >
                                  <FiTrash2 size={16} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Add Color Section */}
                  <div className="add-color-section">
                    <h3>Add Color to Product</h3>

                    {/* Toggle between existing and new colors */}
                    <div style={{ marginBottom: '1rem', display: 'flex', gap: '1rem' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="color-mode"
                          checked={!addingNewColor}
                          onChange={() => setAddingNewColor(false)}
                        />
                        Use Existing Color
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="color-mode"
                          checked={addingNewColor}
                          onChange={() => setAddingNewColor(true)}
                        />
                        Add New Color
                      </label>
                    </div>

                    {/* Existing Colors List */}
                    {!addingNewColor && (
                      <div className="form-group">
                        <label htmlFor="existing-colors">Select from Available Colors ({availableColors.length})</label>
                        {availableColors.length > 0 ? (
                          <select
                            id="existing-colors"
                            value={selectedExistingColor}
                            onChange={(e) => setSelectedExistingColor(e.target.value)}
                            style={{ marginBottom: '1rem' }}
                          >
                            <option value="">-- Choose a color --</option>
                            {availableColors.map((color) => (
                              <option key={color.id} value={color.id}>
                                {color.color_name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <p style={{ color: '#999', fontStyle: 'italic', marginBottom: '1rem' }}>
                            No colors available. Create a new color first.
                          </p>
                        )}
                        {selectedExistingColor && availableColors.find(c => c.id === selectedExistingColor) && (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              marginBottom: '1rem',
                              padding: '0.75rem',
                              backgroundColor: '#f5f5f5',
                              borderRadius: '4px',
                              border: '1px solid #ddd'
                            }}
                          >
                            <div
                              style={{
                                width: '30px',
                                height: '30px',
                                borderRadius: '4px',
                                border: '1px solid #ccc',
                                backgroundColor: availableColors.find(c => c.id === selectedExistingColor)?.color_hex
                              }}
                            />
                            <span>
                              {availableColors.find(c => c.id === selectedExistingColor)?.color_hex}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* New Color Input */}
                    {addingNewColor && (
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="color-name">Color Name</label>
                          <input
                            id="color-name"
                            type="text"
                            value={newColor.name}
                            onChange={(e) => setNewColor({ ...newColor, name: e.target.value })}
                            placeholder="e.g., Black, Red, Navy Blue"
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor="color-hex">Color</label>
                          <div className="color-picker-wrapper">
                            <input
                              id="color-hex"
                              type="color"
                              value={newColor.hex}
                              onChange={(e) => setNewColor({ ...newColor, hex: e.target.value })}
                            />
                            <span className="color-hex-value">{newColor.hex}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Add Button */}
                    <button
                      type="button"
                      className="btn-add-color"
                      onClick={handleAddColor}
                      disabled={saving || (addingNewColor && !newColor.name.trim()) || (!addingNewColor && !selectedExistingColor)}
                    >
                      {addingNewColor ? '+ Add New Color' : '+ Add Selected Color'}
                    </button>
                  </div>
                </fieldset>

                <fieldset className="stock-section">
                  <legend>Stock Management by Size (EU)</legend>

                  {/* Step 1: Enable/Disable Sizes */}
                  <div className="stock-step">
                    <h3>Step 1: Enable Sizes</h3>
                    <p className="step-description">Select which shoe sizes you want to offer for this product</p>

                    <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem' }}>
                      <button
                        type="button"
                        onClick={() => {
                          const allSizes = ['35', '35.5', '36', '36.5', '37', '37.5', '38', '38.5', '39', '39.5', '40', '40.5', '41', '41.5', '42', '43', '44', '45', '46', '47'];
                          const newEnabledSizes = {};
                          const newStockBySize = { ...product.stockBySize };
                          allSizes.forEach(size => {
                            newEnabledSizes[size] = true;
                            if (!newStockBySize[size]) {
                              newStockBySize[size] = 0;
                            }
                          });
                          setEnabledSizes(newEnabledSizes);
                          setProduct({ ...product, stockBySize: newStockBySize });
                        }}
                        style={{
                          padding: '0.5rem 1rem',
                          background: '#28a745',
                          color: 'white',
                          border: 'none',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          fontSize: '0.9rem',
                          fontWeight: '500'
                        }}
                      >
                        ✓ Enable All
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEnabledSizes({});
                          setProduct({ ...product, stockBySize: {} });
                        }}
                        style={{
                          padding: '0.5rem 1rem',
                          background: '#dc3545',
                          color: 'white',
                          border: 'none',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          fontSize: '0.9rem',
                          fontWeight: '500'
                        }}
                      >
                        ✕ Disable All
                      </button>
                    </div>

                    <div className="stock-sizes-grid">
                      {['35', '35.5', '36', '36.5', '37', '37.5', '38', '38.5', '39', '39.5', '40', '40.5', '41', '41.5', '42', '43', '44', '45', '46', '47'].map((size) => (
                        <label key={size} className="size-checkbox">
                          <input
                            type="checkbox"
                            checked={enabledSizes[size] || false}
                            onChange={(e) => {
                              const newEnabledSizes = { ...enabledSizes };
                              const newStockByColor = JSON.parse(JSON.stringify(product.stockByColor)); // Deep copy

                              if (e.target.checked) {
                                // Enable size: ensure all colors have this size (initialized to 0 if not present)
                                newEnabledSizes[size] = true;
                                colors.forEach(color => {
                                  if (!newStockByColor[color.id]) {
                                    newStockByColor[color.id] = {};
                                  }
                                  if (newStockByColor[color.id][size] === undefined) {
                                    newStockByColor[color.id][size] = 0;
                                  }
                                });
                              } else {
                                // Disable size: remove from all colors
                                delete newEnabledSizes[size];
                                Object.keys(newStockByColor).forEach(colorId => {
                                  if (newStockByColor[colorId]) {
                                    delete newStockByColor[colorId][size];
                                  }
                                });
                              }

                              setEnabledSizes(newEnabledSizes);
                              setProduct({ ...product, stockByColor: newStockByColor });
                            }}
                          />
                          <span>{size}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Step 2: Set Inventory */}
                  <div className="stock-step" style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid #eee' }}>
                    <h3>Step 2: Set Inventory by Color & Size</h3>
                    <p className="step-description">Enter quantity in stock for each enabled size and color combination</p>

                    {colors.length === 0 ? (
                      <p style={{ color: '#d9534f', textAlign: 'center', padding: '2rem', fontSize: '0.95rem', background: '#f8d7da', borderRadius: '4px' }}>
                        Please add at least one color to the product first (see Colors section below) before setting inventory.
                      </p>
                    ) : (
                      <div>
                        {colors.map((color) => (
                          <div key={color.id} style={{ marginBottom: '2rem', padding: '1rem', background: '#f9f9f9', borderRadius: '4px', border: '1px solid #eee' }}>
                            <h4 style={{ margin: '0 0 1rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <div style={{ width: '20px', height: '20px', backgroundColor: color.color_hex, borderRadius: '3px', border: '1px solid #ddd' }} />
                              {color.color_name}
                            </h4>
                            <div className="stock-sizes-grid">
                              {['35', '35.5', '36', '36.5', '37', '37.5', '38', '38.5', '39', '39.5', '40', '40.5', '41', '41.5', '42', '43', '44', '45', '46', '47'].map((size) => {
                                if (!enabledSizes[size]) return null;
                                return (
                                  <div key={`${color.id}-${size}`} className="size-input">
                                    <label htmlFor={`size-${color.id}-${size}`}>{size}</label>
                                    <input
                                      id={`size-${color.id}-${size}`}
                                      type="number"
                                      min="0"
                                      value={product.stockByColor[color.id]?.[size] || ''}
                                      onChange={(e) => {
                                        const newStockByColor = { ...product.stockByColor };
                                        if (!newStockByColor[color.id]) {
                                          newStockByColor[color.id] = {};
                                        }
                                        newStockByColor[color.id][size] = e.target.value ? parseInt(e.target.value) : 0;
                                        setProduct({ ...product, stockByColor: newStockByColor });
                                      }}
                                      placeholder="0"
                                    />
                                  </div>
                                );
                              })}
                            </div>
                            {Object.keys(enabledSizes).length === 0 && (
                              <p style={{ color: '#999', textAlign: 'center', padding: '1rem', fontSize: '0.9rem' }}>
                                No sizes enabled. Enable at least one size above.
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </fieldset>
              </div>

              {/* Action Buttons */}
              <div className="editor-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => navigate('/admin')}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-save-product"
                  disabled={saving}
                >
                  <FiSave /> {saving ? 'Saving...' : 'Save Product'}
                </button>
              </div>
            </div>
          </div>

          {/* Replace Color Dialog */}
          {showReplaceColorDialog && colorToReplace && (
            <div className="modal-overlay" onClick={() => setShowReplaceColorDialog(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h2>Replace Color</h2>
                <p>
                  Replace <strong>{colorToReplace.color_name}</strong> with another color
                </p>
                <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1.5rem' }}>
                  Inventory ({getColorInventoryCount(colorToReplace.id)} items) will be transferred to the replacement color.
                </p>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label htmlFor="replacement-color-select">Select replacement color:</label>
                  <select
                    id="replacement-color-select"
                    value={replacementColorId}
                    onChange={(e) => setReplacementColorId(e.target.value ? parseInt(e.target.value) : '')}
                    style={{ marginTop: '0.5rem', padding: '0.5rem', width: '100%' }}
                  >
                    <option value="">-- Select a color --</option>
                    {availableColors.filter(c => c.id !== colorToReplace.id).map((color) => (
                      <option key={color.id} value={color.id}>
                        {color.color_name}
                      </option>
                    ))}
                  </select>
                </div>

                {replacementColorId && (
                  <div style={{ padding: '1rem', background: '#e8f4f8', borderRadius: '4px', marginBottom: '1.5rem' }}>
                    <strong>Preview:</strong>
                    <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem' }}>
                      Transfer {getColorInventoryCount(colorToReplace.id)} items from <strong>{colorToReplace.color_name}</strong> to <strong>
                        {availableColors.find(c => c.id === parseInt(replacementColorId))?.color_name}
                      </strong>
                    </p>
                  </div>
                )}

                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowReplaceColorDialog(false);
                      setReplacementColorId('');
                    }}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      background: '#fff'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleReplaceColor}
                    disabled={!replacementColorId}
                    style={{
                      padding: '0.5rem 1rem',
                      border: 'none',
                      borderRadius: '4px',
                      background: !replacementColorId ? '#ccc' : '#28a745',
                      color: '#fff',
                      cursor: !replacementColorId ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Replace & Transfer Inventory
                  </button>
                </div>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
